'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSaveVoucher } from '@/hooks/react-query/account/use-save-voucher.mutation'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { STORAGE_KEYS } from '@/lib/constants/storage-keys'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { SessionStorage } from '@/lib/utils/session-storage'
import { CheckIcon, Copy } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from './toast'

interface VoucherSuccessPopupProps {
  code: string
  isOpen: boolean
  isSaving: boolean
  onConfirm: () => void
  onClose: () => void
}

function VoucherSuccessPopup({
  code,
  isOpen,
  isSaving,
  onConfirm,
  onClose,
}: VoucherSuccessPopupProps) {
  const t = useTranslations('voucher_url_handler')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!code) return
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 4000)
  }

  return (
    <ResponsiveModal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <ResponsiveModalContent
        side='center'
        className='left-4 right-4 z-[103] gap-4 overflow-visible rounded-2xl border-none bg-white p-0 pb-6 pt-4 lg:left-1/2 lg:right-auto lg:w-[336px] lg:max-w-[336px] lg:-translate-x-1/2 lg:-translate-y-1/2'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ResponsiveModalTitle className='sr-only'>
          {t('discount_headline')}
        </ResponsiveModalTitle>
        <div className='flex flex-col gap-3 px-6'>
          <div className='flex flex-col items-center gap-3 pb-3 pt-6 text-center'>
            <div className='flex w-full flex-col gap-3 text-center text-neutral-900'>
              <p className='font-criteria text-3xl font-bold uppercase leading-[1.1]'>
                {t('discount_headline')}
              </p>
              <p className='whitespace-pre-line font-sans text-base text-neutral-500'>
                {t.raw('slogan')}
              </p>
            </div>

            <div className='flex w-full flex-col gap-3'>
              <div className='relative h-[173px] w-full overflow-hidden rounded'>
                <Image
                  src={ImageHelper.getImageUrl(
                    '/image/2026/07/31/Frame_88250.jpg'
                  )}
                  alt={t('discount_headline')}
                  fill
                  className='object-cover'
                  unoptimized
                />
              </div>

              <div className='flex flex-col gap-1 text-left'>
                <span className='pl-3 font-sans text-sm text-neutral-500'>
                  {t('voucher_code_label')}
                </span>
                {/* onClick đặt ở div bọc ngoài vì icon suffix (absolute, render
                    sau input trong DOM) đè lên trên và chặn mất click nếu gắn
                    thẳng vào Input — bọc ngoài để bấm vào icon vẫn copy được */}
                <div onClick={handleCopy} className='cursor-pointer'>
                  <Input
                    size='lg'
                    readOnly
                    value={code}
                    className='pointer-events-none select-all text-center font-criteria text-lg font-semibold tracking-widest'
                    wrapperClassName='rounded-[56px]'
                    suffix={
                      copied ? (
                        <CheckIcon
                          className='h-4 w-4 shrink-0 text-green-500'
                          strokeWidth={3}
                        />
                      ) : (
                        <Copy className='h-4 w-4 shrink-0 text-neutral-500' />
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <Button
            variant='black'
            size='large'
            onClick={onConfirm}
            disabled={isSaving}
            className='uppercase'
          >
            {isSaving ? t('saving') : t('save_voucher')}
          </Button>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

export function VoucherUrlHandler() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const tVoucher = useTranslations('voucher_url_handler')
  const isLoggedIn = useIsLoggedIn()
  const { mutateAsync: saveVoucher } = useSaveVoucher()
  const [isMounted, setIsMounted] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [voucherCode, setVoucherCode] = useState('')
  const processedRef = useRef<string | null>(null)

  // Đảm bảo component đã mount trên client
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const clearVoucherParam = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('voucher')
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname
    router.replace(newUrl, { scroll: false })
  }, [searchParams, router])

  // Chỉ mở popup + fill sẵn mã từ URL — CHƯA save, đợi user bấm "Mua sắm ngay"
  useEffect(() => {
    if (!isMounted) return
    if (pathname.includes('/doi-tra')) return

    const code = searchParams.get('voucher')?.trim()
    if (code && processedRef.current !== code) {
      processedRef.current = code
      setVoucherCode(code)
    }
  }, [searchParams, isMounted, pathname])

  const handleConfirmSave = useCallback(async () => {
    if (!voucherCode || isSaving) return

    setIsSaving(true)
    try {
      if (isLoggedIn) {
        const response = await saveVoucher(voucherCode)
        toast.message({
          title: response.data.title || tVoucher('title.success'),
          message:
            response.data.description ||
            tVoucher('voucher_saved_to_wallet', { code: voucherCode }),
        })
      } else {
        const saved = SessionStorage.set(
          STORAGE_KEYS.PENDING_VOUCHER,
          voucherCode
        )
        if (!saved) {
          throw new Error(
            tVoucher('cannot_save_voucher', { code: voucherCode })
          )
        }
        toast.message({
          title: tVoucher('title.success'),
          message: tVoucher('voucher_saved_temporary', { code: voucherCode }),
        })
      }
    } catch (error: unknown) {
      // Check if it's the "already saved" error from API response
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error as Error)?.message ||
        tVoucher('error_saving_voucher', { code: voucherCode })

      if (errorMessage.includes(tVoucher('already_saved_keyword'))) {
        const rawMessage = tVoucher.raw('voucher_already_saved') as string
        toast.message({
          title: tVoucher('title.notification'),
          dangerouslyMessage: rawMessage.replace('{code}', voucherCode),
        })
      } else {
        toast.message({
          title: tVoucher('title.error'),
          message: errorMessage,
        })
      }
    } finally {
      setIsSaving(false)
      setVoucherCode('')
      clearVoucherParam()
    }
  }, [
    voucherCode,
    isSaving,
    isLoggedIn,
    saveVoucher,
    tVoucher,
    clearVoucherParam,
  ])

  const handleClosePopup = () => {
    setVoucherCode('')
    clearVoucherParam()
  }

  return (
    <>
      {voucherCode && (
        <VoucherSuccessPopup
          code={voucherCode}
          isOpen={!!voucherCode}
          isSaving={isSaving}
          onConfirm={handleConfirmSave}
          onClose={handleClosePopup}
        />
      )}
    </>
  )
}
